import React, { useContext, useEffect, useState } from "react";
import cc from "classcat";
import { useLocalization } from "@fluent/react";
import { RRule } from "rrule";
import { Tabs } from "@narmi/design_system";
import ApiHttp from "byzantine/src/ApiHttp";
import BillPaySchedule from "byzantine/src/BillPaySchedule";
import Payee from "byzantine/src/Payee";
import type Account from "byzantine/src/Account";
import type { ReactNode } from "react";
import { useNotificationContext } from "cerulean";
import AccountContext from "../contexts/AccountContext";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import SectionCard from "../SectionCard";
import TransferTable from "./TransferTable";

type ScheduledTransfer = API.Transfer & {
  kind: "scheduled_transfer";
  from_account: Account | null;
  to_account: Account | null;
  rrule: RRule;
};

type ScheduledACHPayment = API.ScheduledACHPayment & {
  kind: "scheduled_ach_payment";
  recipientName?: string;
  rrule: RRule;
};

type AwaitingApprovalACHPayment = API.ACHPayment & {
  kind: "awaiting_approval_ach_payment";
  recipientName?: string;
  next_transfer_at: string;
};

export type SortedTransaction =
  | ScheduledTransfer
  | ScheduledACHPayment
  | AwaitingApprovalACHPayment;

type ScheduledTransfersCardProps = {
  accountUuid: Account["id"];
  limits?: API.Limits.DisplayLimits;
  recipients?: API.Recipient[];
};

const sortTransactions = (
  scheduledTransfers: API.Transfer[] | undefined,
  scheduledACHPayments: API.ScheduledACHPayment[] | undefined,
  awaitingApprovalACHPayments: API.ACHPayment[] | undefined,
  recipients: API.Recipient[] | undefined,
  accounts: Account[],
  accountUuid: Account["id"]
) => {
  /* separates the fetched scheduled transfers into one-time transfers and recurring transfers */
  const oneTimeTransactions: SortedTransaction[] = [];
  const recurringTransactions: SortedTransaction[] = [];

  scheduledTransfers?.forEach((t) => {
    const transfer: ScheduledTransfer = {
      ...t,
      kind: "scheduled_transfer",
      from_account: accounts.find((a) => a.id === t.from_account_id) || null,
      to_account: accounts.find((a) => a.id === t.to_account_id) || null,
      rrule: {} as RRule,
    };

    /* only display scheduled transfers where the account is either the source or destination */
    if (
      ![transfer.from_account?.id, transfer.to_account?.id].includes(
        accountUuid
      )
    ) {
      return;
    }

    /* only display active scheduled transfers (one-time transfers are not marked as deleted when
       they are completed, and this table is for upcoming transfers)
     */
    if (transfer.state === "expired") return;

    transfer.rrule = RRule.fromString(t.recurring_rule);
    if (transfer.rrule.options.count === 1) {
      oneTimeTransactions.push(transfer);
    } else {
      recurringTransactions.push(transfer);
    }
  });

  scheduledACHPayments?.forEach((payment) => {
    const updatedPayment: ScheduledACHPayment = {
      ...payment,
      kind: "scheduled_ach_payment",
      recipientName: recipients?.find(
        (recipient) => recipient.id === payment.recipient
      )?.name,
      rrule: {} as RRule,
    };

    /* only display scheduled ACH payments where the account is institution_account */
    if (payment.institution_account !== accountUuid) {
      return;
    }

    updatedPayment.rrule = RRule.fromString(payment.recurring_rule);
    if (updatedPayment.rrule.options.count === 1) {
      oneTimeTransactions.push(updatedPayment);
    } else {
      recurringTransactions.push(updatedPayment);
    }
  });

  // "one time regular" ach payments
  awaitingApprovalACHPayments?.forEach((payment) => {
    const updatedPayment: AwaitingApprovalACHPayment = {
      ...payment,
      kind: "awaiting_approval_ach_payment",
      recipientName: recipients?.find(
        (recipient) => recipient.id === payment.recipient
      )?.name,
      next_transfer_at: "",
    };

    /* only display awaiting approval ACH payments where the account is institution_account */
    if (payment.institution_account !== accountUuid) {
      return;
    }

    updatedPayment.next_transfer_at = payment.created_at;
    oneTimeTransactions.push(updatedPayment);
  });

  const sortByNextTransferAt = <T extends { next_transfer_at: string }>(
    values: T[]
  ) =>
    values.sort(
      (a, b) =>
        new Date(a.next_transfer_at).getTime() -
        new Date(b.next_transfer_at).getTime()
    );

  const sortedOneTimeTransactions = sortByNextTransferAt(oneTimeTransactions);
  const sortedRecurringTransactions = sortByNextTransferAt(
    recurringTransactions
  );

  return {
    sortedOneTimeTransactions,
    sortedRecurringTransactions,
  };
};

const ScheduledTransfersCard = ({
  accountUuid,
  limits,
  recipients,
}: ScheduledTransfersCardProps) => {
  /* a card that displays one-time and recurring scheduled transfers/payments in separate tabs
  where the account is either the source or the destination */
  const features = useUserFeatures();
  const { l10n } = useLocalization();
  const achPaymentsEnabled = features.ach_payments;

  const [oneTimeTransactions, setOneTimeTransactions] = useState<
    SortedTransaction[]
  >([]);
  const [recurringTransactions, setRecurringTransactions] = useState<
    SortedTransaction[]
  >([]);
  const [isLoadingTransfers, setIsLoadingTransfers] = useState(true);

  const [payees, setPayees] = useState<Payee[]>([]);
  const [payments, setPayments] = useState<BillPaySchedule[]>([]);
  const oneTimePayments = payments.filter((p) => !p.is_recurring);
  const recurringPayments = payments.filter((p) => p.is_recurring);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);

  const [isLoading, setIsLoading] = useState(true);
  const { accounts } = useContext(AccountContext);
  const currentAccount = accounts.find((a) => a.id === accountUuid);
  const { sendNotification } = useNotificationContext();

  const fetchScheduledTransfers = async (): Promise<
    API.Transfer[] | undefined
  > => {
    const { scheduled_transfers: scheduledTransfers } = await ApiHttp.fetch<{
      scheduled_transfers?: API.Transfer[];
    }>("transfers/scheduled?is_expired=false", {
      method: "GET",
    });

    return scheduledTransfers;
  };

  const fetchScheduledACHPayments = async (): Promise<
    API.ScheduledACHPayment[]
  > => {
    const scheduledACHPayments = achPaymentsEnabled
      ? (
          await ApiHttp.fetch<{ results: API.ScheduledACHPayment[] }>(
            "unverified_ach_transactions/scheduled?exclude_states=schedule_rejected,expired",
            { method: "GET" }
          )
        ).results
      : [];

    return scheduledACHPayments;
  };

  const fetchAwaitingApprovalACHPayments = async (): Promise<
    API.ACHPayment[]
  > => {
    const achPayments = achPaymentsEnabled
      ? (
          await ApiHttp.fetch<{ results: API.ACHPayment[] }>(
            "ach_payments?state=awaiting_approval",
            {
              method: "GET",
            }
          )
        ).results
      : [];

    return achPayments;
  };

  const fetchTransactions = async () => {
    try {
      setIsLoadingTransfers(true);
      /* resetting the one-time and recurring transfer states because this function
         will be called again when a transfer is deleted */
      setOneTimeTransactions([]);
      setRecurringTransactions([]);

      // fetch scheduled transfers
      const scheduledTransfers = await fetchScheduledTransfers();

      // fetch active scheduled ACH payments (future one time, or recurring, can be pending approval)
      // or have been approved
      const scheduledACHPayments = await fetchScheduledACHPayments();

      // fetch all ACH Payments that are pending dual approval
      const awaitingApprovalACHPayments =
        await fetchAwaitingApprovalACHPayments();

      const { sortedOneTimeTransactions, sortedRecurringTransactions } =
        sortTransactions(
          scheduledTransfers,
          scheduledACHPayments,
          awaitingApprovalACHPayments,
          recipients,
          accounts,
          accountUuid
        );

      setOneTimeTransactions(sortedOneTimeTransactions);
      setRecurringTransactions(sortedRecurringTransactions);
    } catch {
      sendNotification({
        type: "negative",
        text: "There was an error retrieving scheduled transfers, please try again later.",
      });
    } finally {
      setIsLoadingTransfers(false);
    }
  };

  const fetchPayees = async () => {
    try {
      setPayees(await Payee.fetchPayees());
    } catch {
      sendNotification({
        type: "negative",
        text: "Error fetching your payees. Please contact support.",
      });
    }
  };

  const fetchPayments = async () => {
    try {
      setIsLoadingPayments(true);
      const allPayments = await BillPaySchedule.fetchPayments(accountUuid);
      setPayments(BillPaySchedule.filterScheduledPayments(allPayments));
    } catch {
      sendNotification({
        type: "negative",
        text: "Error fetching your payments. Please contact support.",
      });
    } finally {
      setIsLoadingPayments(false);
    }
  };

  useEffect(() => {
    // update the `payees` field of every payment once payees and payments have been fetched
    if (payees?.length && payments?.length) {
      setPayments((prevPayments: BillPaySchedule[]) =>
        prevPayments.map((p) => {
          p.payees = payees;
          return p;
        })
      );
    }
  }, [JSON.stringify(payees), JSON.stringify(payments)]);

  useEffect(() => {
    fetchTransactions();
    if (currentAccount?.isValidBillpaySource()) {
      fetchPayees();
      fetchPayments();
    } else {
      setIsLoadingPayments(false);
    }
  }, []);

  /* remove loading state only if both transfers and payments are done loading */
  useEffect(() => {
    if (!isLoadingPayments && !isLoadingTransfers) setIsLoading(false);
    else setIsLoading(true);
  }, [isLoadingPayments, isLoadingTransfers]);

  // only display card if there are scheduled transfers for the account
  const isCardDisplayed =
    oneTimeTransactions.length > 0 ||
    recurringTransactions.length > 0 ||
    payments.length > 0;

  // only diplay tabs if both recurring and one-time transfers exist
  const areTabsDisplayed =
    (oneTimeTransactions.length > 0 || oneTimePayments.length > 0) &&
    (recurringTransactions.length > 0 || recurringPayments.length > 0);

  const untabbedTable: ReactNode = (
    <TransferTable
      hasTopBorder={true}
      transfers={
        recurringTransactions.length > 0
          ? recurringTransactions
          : oneTimeTransactions
      }
      payments={
        recurringPayments.length > 0 ? recurringPayments : oneTimePayments
      }
      accountUuid={accountUuid}
      isRecurring={
        recurringTransactions.length > 0 || recurringPayments.length > 0
      }
      limits={limits}
      fetchScheduledTransfers={fetchTransactions}
      fetchPayments={fetchPayments}
    />
  );

  const tabbedTable: ReactNode = (
    <Tabs defaultSelectedIndex={0}>
      <Tabs.List xPadding="l">
        <Tabs.Tab
          label={l10n.getString("transactions-tab-one-time", null, "One time")}
          tabId="oneTime"
        />
        <Tabs.Tab
          label={l10n.getString(
            "transactions-tab-recurring",
            null,
            "Recurring"
          )}
          tabId="recurring"
        />
      </Tabs.List>
      <Tabs.Panel tabId="oneTime">
        <div className="padding--x--l padding--bottom--xs">
          <TransferTable
            hasTopBorder={false}
            transfers={oneTimeTransactions}
            payments={oneTimePayments}
            accountUuid={accountUuid}
            isRecurring={false}
            limits={limits}
            fetchScheduledTransfers={fetchTransactions}
            fetchPayments={fetchPayments}
          />
        </div>
      </Tabs.Panel>
      <Tabs.Panel tabId="recurring">
        <div className="padding--x--l padding--bottom--xs">
          <TransferTable
            hasTopBorder={false}
            transfers={recurringTransactions}
            payments={recurringPayments}
            accountUuid={accountUuid}
            isRecurring={true}
            limits={limits}
            fetchScheduledTransfers={fetchTransactions}
            fetchPayments={fetchPayments}
          />
        </div>
      </Tabs.Panel>
    </Tabs>
  );

  return (
    <SectionCard
      isLoading={isLoading}
      paddingSize={areTabsDisplayed ? null : "l"}
      kind={areTabsDisplayed ? undefined : "transactions"}
    >
      {isCardDisplayed && (
        <>
          <div
            className={cc([
              "padding--bottom--s",
              {
                "padding--x--l padding--top--l": areTabsDisplayed,
              },
            ])}
          >
            <SectionCard.Title
              text={l10n.getString(
                "heading-scheduled-pending-transactions",
                null,
                "Scheduled and pending transactions"
              )}
            />
          </div>
          {areTabsDisplayed ? tabbedTable : untabbedTable}
        </>
      )}
    </SectionCard>
  );
};

ScheduledTransfersCard.displayName = "ScheduledTransfersCard";

export default ScheduledTransfersCard;
